import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import HeroSection from '../components/ui/HeroSection';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Divider from '../components/ui/Divider';
import { HotelIcon, MapPinIcon, CustomerServiceIcon, AuctionIcon, HomeIcon, EventIcon, ExperienceIcon } from '../utils/IconWrapper';
import { useLanguage } from '../i18n/LanguageContext';
import HeaderBanner from '../components/ui/HeaderBanner';
import LiveVisitorCounter from '../components/ui/LiveVisitorCounter';

// Immagini temporanee da sostituire con reali quando disponibili
const heroImage = 'https://luxuryinwhistler.com/wp-content/uploads/2023/06/14CedarRidge12Dec05ALOHA-1.jpg';
const servicesImage = 'https://images.unsplash.com/photo-1521783988139-89397d761dce?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const accommodationImage1 = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80';
const accommodationImage2 = 'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const accommodationImage3 = 'https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const attractionImage = 'https://images.unsplash.com/photo-1558383817-8fbd5164d689?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const aboutImage = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(motion.div)`
  background-color: var(--bg-secondary);
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
  }
`;

const IconWrapper = styled.div`
  font-size: 2.5rem;
  color: var(--accent-primary);
  margin-bottom: 1.5rem;
`;

const FeatureTitle = styled.h3`
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const FeatureDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
`;

const ViewAllLink = styled.div`
  margin-top: 3rem;
  text-align: center;
`;

const AboutRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutImage = styled.div`
  position: relative;
  height: 500px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    right: -15px;
    width: 85%;
    height: 85%;
    border: 3px solid var(--accent-primary);
    z-index: -1;
  }
  
  @media (max-width: 992px) {
    height: 400px;
  }
`;

const AboutContent = styled.div`
  padding: 2rem 0;
`;

const AboutTitle = styled.h2`
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 0;
    width: 60px;
    height: 2px;
    background-color: var(--accent-primary);
  }
`;

const AboutText = styled.p`
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.7;
`;

const DiscoverButton = styled(Button)`
  margin-top: 1.5rem;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 600px;
  margin: 0 auto 3rem;
  color: var(--text-secondary);
`;

const Home: React.FC = () => {
  const { t, language } = useLanguage();
  
  return (
    <>
      <HeaderBanner />
      <LiveVisitorCounter />
      
      {/* 1. Hero Principale - Network */}
      <HeroSection
        title={language === 'it' ? 'Il primo vero network immobiliare' : 'The first real estate network'}
        backgroundImage={heroImage}
        height="90vh"
      />
      
      <Divider variant="diamond" />
      
      {/* 2. Hero Alloggi */}
      <HeroSection
        title={language === 'it' ? 'Alloggi' : 'Accommodations'}
        subtitle={language === 'it' ? 'Varie tipologie di struttura' : 'Various types of properties'}
        backgroundImage={accommodationImage1}
        buttonText={language === 'it' ? 'Scopri' : 'Discover'}
        buttonLink="/accommodations"
        height="70vh"
      />
      
      <Divider variant="diamond" />
      
      {/* 3. Hero Servizi */}
      <HeroSection
        title={language === 'it' ? 'Servizi' : 'Services'}
        subtitle={language === 'it' 
          ? 'Scopri la nostra gamma completa di servizi premium' 
          : 'Discover our complete range of premium services'}
        backgroundImage={servicesImage}
        buttonText={language === 'it' ? 'Richiedi' : 'Request'}
        buttonLink="/services"
        height="70vh"
      />
      
      <Divider variant="diamond" />
      
      {/* 5. Sezione Chi Siamo - rimossa e spostata in About */}
      
      {/* Il Footer viene gestito dal layout e appare automaticamente */}
    </>
  );
};

export default Home; 